import http from '@utils/http';
import { ENV } from '@configs/env';
import { appQueryClient } from '@app/appQueryClient';

const LOCAL_STORAGE_KEY = 'sprinto_app_notifications';
export const NOTIFICATIONS_QUERY_KEY = ['notifications'];

export const getStoredNotifications = () => {
    try {
        const raw = localStorage.getItem(LOCAL_STORAGE_KEY);
        if (raw) {
            const parsed = JSON.parse(raw);
            if (Array.isArray(parsed)) return parsed;
        }
    } catch {
        // ignore storage errors
    }
    return [];
};

export const saveStoredNotifications = (items) => {
    try {
        localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(items));
    } catch {
        // ignore storage errors
    }
};

export const addNotification = (notification) => {
    const existing = getStoredNotifications();
    const newItem = {
        id: notification.id || `notif-${Date.now()}-${Math.random().toString(36).substring(2, 6)}`,
        title: notification.title,
        message: notification.message || notification.body,
        body: notification.body || notification.message,
        type: notification.type || 'bug_report',
        read: false,
        readAt: null,
        createdAt: new Date().toISOString(),
        metadata: notification.metadata || {},
    };
    const updated = [newItem, ...existing];
    saveStoredNotifications(updated);

    if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('app_notification_received', { detail: newItem }));
        window.dispatchEvent(new CustomEvent('sprinto_notification_added', { detail: newItem }));
    }

    try {
        appQueryClient.invalidateQueries({ queryKey: NOTIFICATIONS_QUERY_KEY });
    } catch {
        // ignore
    }

    return newItem;
};

/**
 * Normalize notification item: API returns readAt (ISO string | null); we add read boolean.
 */
const normalizeItem = (item) => ({
    ...item,
    read: !!(item.readAt ?? item.read_at ?? item.read),
});

/**
 * GET /api/v1/notifications
 */
export const getNotifications = (params = {}) => {
    return http
        .get('/notifications', { params })
        .then((res) => {
            const raw = res.data?.data ?? res.data;
            const data = Array.isArray(raw) ? raw : (raw?.items ?? raw?.data ?? []);
            const apiItems = data.map(normalizeItem);
            const localItems = getStoredNotifications().map(normalizeItem);

            const seen = new Set();
            const combined = [];
            for (const item of [...localItems, ...apiItems]) {
                if (!seen.has(item.id)) {
                    seen.add(item.id);
                    combined.push(item);
                }
            }
            return { items: combined, total: combined.length };
        })
        .catch(() => {
            const localItems = getStoredNotifications().map(normalizeItem);
            return { items: localItems, total: localItems.length };
        });
};

/**
 * GET /api/v1/notifications/count
 */
export const getNotificationsUnreadCount = () =>
    http
        .get('/notifications/count')
        .then((res) => {
            const raw = res.data?.data ?? res.data;
            let count = 0;
            if (typeof raw === 'number' && Number.isFinite(raw)) count = Math.max(0, Math.floor(raw));
            else if (raw && typeof raw === 'object') {
                const n = raw.unreadCount ?? raw.unread_count ?? raw.unread ?? raw.count;
                if (typeof n === 'number' && Number.isFinite(n)) count = Math.max(0, Math.floor(n));
            }
            const localUnread = getStoredNotifications().filter((n) => !n.read && !n.readAt).length;
            return count + localUnread;
        })
        .catch(() => {
            return getStoredNotifications().filter((n) => !n.read && !n.readAt).length;
        });

/**
 * PATCH /api/v1/notifications/:id/read
 */
export const markNotificationRead = (id) => {
    const localItems = getStoredNotifications();
    const updated = localItems.map((n) => (n.id === id ? { ...n, read: true, readAt: new Date().toISOString() } : n));
    saveStoredNotifications(updated);

    try {
        appQueryClient.invalidateQueries({ queryKey: NOTIFICATIONS_QUERY_KEY });
    } catch {
        // ignore
    }

    return http
        .patch(`/notifications/${id}/read`)
        .then((res) => res.data?.data ?? res.data)
        .catch(() => ({ success: true }));
};

/**
 * PATCH /api/v1/notifications/read-all
 */
export const markAllNotificationsRead = () => {
    const localItems = getStoredNotifications();
    const updated = localItems.map((n) => ({ ...n, read: true, readAt: new Date().toISOString() }));
    saveStoredNotifications(updated);

    try {
        appQueryClient.invalidateQueries({ queryKey: NOTIFICATIONS_QUERY_KEY });
    } catch {
        // ignore
    }

    return http
        .patch('/notifications/read-all')
        .then((res) => res.data?.data ?? res.data)
        .catch(() => ({ success: true }));
};

export const getNotificationsStreamUrl = () => {
    const raw = (ENV.API_URL_STREAM && String(ENV.API_URL_STREAM).trim()) || ENV.API_URL || '';
    const base = raw.replace(/\/$/, '');
    return `${base}/notifications/stream`;
};
